// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ProfileEffectCommands from '@app/features/user/commands/ProfileEffectCommands';
import {registerCosmeticPoster} from '@app/features/user/state/CosmeticPosterRegistry';
import {rewriteLegacyApiHostUrl} from '@app/features/user/utils/LegacyApiHostRewrite';
import type {ProfileEffectCatalogEntryResponse} from '@fluxer/schema/src/domains/user/ProfileEffectSchemas';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('ProfileEffectCatalog');

let inflightLoad: Promise<void> | null = null;

class ProfileEffectCatalog {
	effects: Array<ProfileEffectCatalogEntryResponse> = [];
	loaded = false;
	loading = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	getById(id: string | null | undefined): ProfileEffectCatalogEntryResponse | undefined {
		if (!id) {
			return undefined;
		}
		return this.effects.find((entry) => entry.id === id);
	}

	async load(): Promise<void> {
		if (this.loaded && this.effects.length > 0) {
			return;
		}
		if (inflightLoad) {
			return inflightLoad;
		}
		inflightLoad = this.fetchCatalog();
		try {
			await inflightLoad;
		} finally {
			inflightLoad = null;
		}
	}

	private async fetchCatalog(): Promise<void> {
		this.loading = true;
		try {
			const response = await ProfileEffectCommands.fetchProfileEffectCatalog();
			runInAction(() => {
				this.effects = response.profile_effects.map((entry) => ({
					...entry,
					image: rewriteLegacyApiHostUrl(entry.image),
					...(entry.poster ? {poster: rewriteLegacyApiHostUrl(entry.poster)} : {}),
				}));
				for (const entry of this.effects) registerCosmeticPoster(entry.image, entry.poster);
				this.loaded = true;
			});
		} catch (error) {
			logger.warn('Failed to load profile effect catalog', error);
			runInAction(() => {
				this.effects = [];
				this.loaded = true;
			});
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}
}

export default new ProfileEffectCatalog();
